import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from './apiAuth';
import { authHeader, userEmail, canonicalizeResponse } from './common';
import { notification } from 'antd';

export const listSurveys = createAsyncThunk(
  'survey/listSurveys',
  async (filterdata, { rejectWithValue }) => {
    try {
      const response = await api.post('/profile-surveys/available', filterdata, {
        headers: await authHeader(),
      });
      return canonicalizeResponse(response);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to list surveys');
    }
  }
);
export const surveyDetail = createAsyncThunk(
  'survey/surveyDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/profile-surveys/${id}`, {
        headers: await authHeader(),
      });
     
      return canonicalizeResponse(response);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to notify user');
    }
  }
);

 
export const saveSurvey = createAsyncThunk(
  'survey/saveSurvey',
  async ({ surveyId, responsedata }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/profile-surveys/${surveyId}/responses`, responsedata, {
        headers: await authHeader(),
      });
     notification.success({
        message: 'Survey Response Submitted Successfully',
      });
      return canonicalizeResponse(response);
    } catch (error) {
      notification.error({  message: error.response?.data?.message || 'Failed to submit survey response ' });
      return rejectWithValue(error.response?.data?.message || 'Failed to submit survey response');
    }
  }
);

// Fetch live surveys from external API
export const listLiveSurveys = createAsyncThunk(
  'survey/listLiveSurveys',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/surveys/live', {
        headers: await authHeader(),
      });
      return canonicalizeResponse(response);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch live surveys');
    }
  }
);

// Get user's assigned surveys
export const getUserSurveyList = createAsyncThunk(
  'survey/getUserSurveyList',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/surveys/live', {
        headers: await authHeader(),
        params: params,
      });
      return canonicalizeResponse(response);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user surveys');
    }
  }
);

// Get pre-screener questions for a quota
export const getPreScreenerQuestions = createAsyncThunk(
  'survey/getPreScreenerQuestions',
  async (quotaId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/surveys/pre-screener/${quotaId}`, {
        headers: await authHeader(),
      });
      return canonicalizeResponse(response);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch pre-screener questions');
    }
  }
);

// Submit pre-screener answers
export const submitPreScreenerAnswers = createAsyncThunk(
  'survey/submitPreScreenerAnswers',
  async (answers, { rejectWithValue }) => {
    try {
      const response = await api.post('/surveys/pre-screener/submit', answers, {
        headers: await authHeader(),
      });
      return canonicalizeResponse(response);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to submit pre-screener answers');
    }
  }
);




// Fetch provider/member surveys
export const fetchProviderSurveys = createAsyncThunk(
  'survey/fetchProviderSurveys',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/member/surveys', {
        headers: await authHeader(),
      });
      return canonicalizeResponse(response);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch provider surveys');
    }
  }
);

const initialState = {
  surveys: [], 
  liveSurveys: [],
  liveSurveysLoading: false,
  surveyLoading: null,
  detailSurvey: [],
  isLoading: false,
  saveDetail: [],
  filterData: JSON.parse(localStorage.getItem('filterData')) || null,  
  status: 'idle', 
  error: null,
  fetching: false,
  preScreenerQuestions: null,
  providerSurveys: [],
  providerSurveysLoading: false,
};

const surveySlice = createSlice({
  name: 'survey',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    // Clear current survey
    clearCurrentSurvey: (state) => {
      state.currentSurvey = null;
    },
  },
  extraReducers: (builder) => {
    // getSurvey
    builder
      // listSurveys
      .addCase(listSurveys.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.surveyLoading = true;
      })
      .addCase(listSurveys.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.surveys = action.payload;
        state.error = null;
        state.surveyLoading = false;
      })
      .addCase(listSurveys.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
        state.surveyLoading = false;
      })
      

    .addCase(surveyDetail.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(surveyDetail.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.detailSurvey = action.payload;
        state.error = null;
      })
      .addCase(surveyDetail.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      .addCase(saveSurvey.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(saveSurvey.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.saveDetail = action.payload;
        state.error = null;
      })
      .addCase(saveSurvey.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // listLiveSurveys
      .addCase(listLiveSurveys.pending, (state) => {
        state.liveSurveysLoading = true;
        state.error = null;
      })
      .addCase(listLiveSurveys.fulfilled, (state, action) => {
        state.liveSurveysLoading = false;
        state.liveSurveys = action.payload;
        state.error = null;
      })
      .addCase(listLiveSurveys.rejected, (state, action) => {
        state.liveSurveysLoading = false;
        state.error = action.payload;
      })

      // getUserSurveyList
      .addCase(getUserSurveyList.pending, (state) => {
        state.fetching = true;
        state.error = null;
      })
      .addCase(getUserSurveyList.fulfilled, (state, action) => {
        state.fetching = false;
        state.surveys = action.payload;
        state.error = null;
      })
      .addCase(getUserSurveyList.rejected, (state, action) => {
        state.fetching = false;
        state.error = action.payload;
      })

      // getPreScreenerQuestions
      .addCase(getPreScreenerQuestions.pending, (state) => {
        state.fetching = true;
        state.error = null;
      })
      .addCase(getPreScreenerQuestions.fulfilled, (state, action) => {
        state.fetching = false;
        state.preScreenerQuestions = action.payload;
        state.error = null;
      })
      .addCase(getPreScreenerQuestions.rejected, (state, action) => {
        state.fetching = false;
        state.error = action.payload;
      })

      // submitPreScreenerAnswers
      .addCase(submitPreScreenerAnswers.pending, (state) => {
        state.fetching = true;
        state.error = null;
      })
      .addCase(submitPreScreenerAnswers.fulfilled, (state, action) => {
        state.fetching = false;
        state.error = null;
      })
      .addCase(submitPreScreenerAnswers.rejected, (state, action) => {
        state.fetching = false;
        state.error = action.payload;
      })

      // fetchProviderSurveys
      .addCase(fetchProviderSurveys.pending, (state) => {
        state.providerSurveysLoading = true;
        state.error = null;
      })
      .addCase(fetchProviderSurveys.fulfilled, (state, action) => {
        state.providerSurveysLoading = false;
        state.providerSurveys = action.payload;
        state.error = null;
      })
      .addCase(fetchProviderSurveys.rejected, (state, action) => {
        state.providerSurveysLoading = false;
        state.error = action.payload;
      });
      
  },
});

export const { clearError, clearCurrentSurvey } = surveySlice.actions;

export default surveySlice.reducer;